import React, { useState, useEffect, useRef, useCallback } from 'react';

import { useAuth } from '../../context/AuthContext';
import { useChatInputValue } from '../../pages/usePlanningPageState';
import { errorMessage } from '../../services/apiError';
import { eventService } from '../../services/eventService';
import { invokeExactPlanningAction } from '../../services/planningActionRegistry';
import { profileService } from '../../services/profileService';
import { projectService } from '../../services/projectService';
import { teamService, type AiCreditQuotaStatus } from '../../services/teamService';
import { UpgradeModal } from '../BillingModal';
import { useToast } from '../Toast';

import { BYOKSettingsModal } from './BYOKSettingsModal';
import { ChatBoxInput, ModelTier } from './ChatBoxInput';
import { dispatchBuildFlowResolved, useBuildProgress, useBuildTrigger, useStartBuildListener } from './StartBuildingButton';

const PLAN_TO_DEFAULT_MODEL: Record<string, ModelTier> = {
  free: 'super_large',
  standard: 'super_large',
  standard_india: 'super_large',
  byok: 'byok_super_large',
  plus: 'super_large',
  pro: 'super_large',
};

const getDefaultModel = (tier?: string, hasByok?: boolean): ModelTier => {
  if (hasByok && tier !== 'free') return 'byok_super_large';

  return PLAN_TO_DEFAULT_MODEL[tier?.toLowerCase() ?? ''] ?? 'super_large';
};

const isByokModel = (model: ModelTier): boolean => model.startsWith('byok_');

/** Reuse the auth bootstrap flag; refresh only after a key mutation. */
const useBYOKKeys = (initialHasKeys: boolean) => {
  const [refreshedHasKeys, setRefreshedHasKeys] = useState<boolean | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;

    return () => { mounted.current = false; };
  }, []);

  const refreshKeys = () => {
    profileService.getBYOKKeys()
      .then((d) => { if (mounted.current) setRefreshedHasKeys((d.count || 0) > 0); })
      .catch(() => {});
  };

  return { hasByokKeys: refreshedHasKeys ?? initialHasKeys, refreshKeys };
};

type ChatBoxInputAreaProps = {
  isLoading: boolean;
  onInputChange: (val: string) => void;
  onSend: (val: string, mediaUrls?: string[]) => void;
  onAgentLogsClick: () => void;
  inputRef: React.RefObject<HTMLTextAreaElement | null>;
  projectId: string | null;
  sessionId?: string | null;
  isAnyAgentRunning: boolean;
  isPlanningRunning: boolean;
  hasTodoTasks: boolean;
  userMessageCount: number;
  isReadOnly?: boolean;
};

type QueuedDeveloperMessage = {
  id: string;
  session_id: string;
  content: string;
  state: string;
};

/** Read-only sessions can't build; still resolve the click so the
 *  dispatching button doesn't spin forever waiting for build_flow_resolved. */
const resolveBuildAsReadOnly = (_skipNavigation?: boolean, clickId?: string) => {
  dispatchBuildFlowResolved(false, clickId, 'read_only');
};

/** Headless listener for trigger_start_build events (no visible UI). */
const useHeadlessBuildListener = (
  projectId: string | null, selectedModel: ModelTier,
  hasByokKeys: boolean, onOpenSettings: () => void,
  enabled: boolean,
) => {
  const progress = useBuildProgress(projectId, selectedModel);
  const { handleBuild, showUpgradeModal, setShowUpgradeModal } = useBuildTrigger(
    projectId, selectedModel, progress, hasByokKeys, onOpenSettings,
  );

  useStartBuildListener(enabled ? handleBuild : resolveBuildAsReadOnly);

  return { showUpgradeModal, setShowUpgradeModal };
};

/** Check if the active project credit source has enough credits (>= 1) to proceed. */
const hasEnoughCredits = async (projectId?: string | null): Promise<boolean> => {
  if (projectId) {
    try {
      const status = await projectService.getProjectCreditSource(projectId);
      if (status.is_unlimited) return true;
      const availableCredits = status.credit_billing_source === 'org_credit' && status.can_use_org_credit
        ? status.org_credits_balance
        : status.own_credits_balance;

      return availableCredits >= 1;
    } catch {
      // Fail open and let the backend's authoritative credit gate decide.
      return true;
    }
  }

  const { subscriptionService } = await import('../../services/subscriptionService');
  const sub = await subscriptionService.getCurrentSubscription();

  return Boolean(sub.is_unlimited) || sub.credits_balance >= 1;
};

const blockOnLowCredits = async (): Promise<boolean> => {
  const { signalInsufficientCredits } = await import('../../services/creditGateEvents');

  signalInsufficientCredits();

  return true;
};

type ResumePhase = 'user_flow' | 'first_page' | 'all_pages' | 'review_build' | 'auto';

const normalizeResumePhase = (phase?: string | null): ResumePhase => {
  if (
    phase === 'user_flow'
    || phase === 'first_page'
    || phase === 'all_pages'
    || phase === 'review_build'
    || phase === 'auto'
  ) return phase;

  return 'auto';
};

const doResume = async (
  projectId: string, phase?: string | null, sessionId?: string | null, planningActionInvocationId?: string,
  designTier?: 'basic' | 'extreme',
) => {
  const { chatService } = await import('../../services/chatService');

  await chatService.resumeDesign(
    projectId, normalizeResumePhase(phase), sessionId, planningActionInvocationId, designTier,
  );
};

interface DesignResumeDetail {
  planningActionInvocationId?: string;
  designTier?: 'basic' | 'extreme';
  artifactType?: string | null;
  phase?: string | null;
  entryId?: string | null;
}

const requestDesignDataRefresh = (source: string, projectId: string) => {
  window.dispatchEvent(new globalThis.CustomEvent('design_data_refresh_requested', {
    detail: { source, projectId },
  }));
};

const emitAgentCancelEvents = (
  projectId: string,
  sessionId: string | null,
  cancelledPhase?: string | null,
) => {
  eventService.emit('agent_log', {
    type: 'agent_log',
    project_id: projectId,
    session_id: sessionId,
    agent_type: 'Tech Lead Agent',
    action: 'cancelled',
    summary: 'Cancelled by user',
    created_at: new Date().toISOString(),
  });
  eventService.emit('planning_complete', {
    type: 'planning_complete',
    project_id: projectId,
    session_id: sessionId,
    status: 'cancelled',
    cancelled_phase: cancelledPhase ?? 'auto',
  });
};

const guardCredits = async (projectId?: string | null): Promise<boolean> => {
  const ok = await hasEnoughCredits(projectId);

  if (ok) return false;

  await blockOnLowCredits();

  return true;
};

const handleDesignResume = async (
  projectId: string,
  sessionId?: string | null,
  detail?: DesignResumeDetail,
  onError?: (message: string) => void,
) => {
  // ok:true only when the resume POST actually succeeded; the credit-guard
  // block and any error resolve with ok:false so Continue buttons stay
  // actionable instead of latching a false "Continued" state.
  let ok = false;

  try {
    if (await guardCredits(projectId)) return;

    await doResume(projectId, detail?.phase, sessionId, detail?.planningActionInvocationId, detail?.designTier);
    ok = true;
    requestDesignDataRefresh('resume-design', projectId);

    // Instead of forced redirection, scroll the chat to bottom to bring
    // the newly triggered build or design status into view.
    setTimeout(() => {
      window.dispatchEvent(new Event('scroll_to_chat_bottom'));
    }, 100);
    setTimeout(() => {
      window.dispatchEvent(new Event('scroll_to_chat_bottom'));
    }, 500);
  } catch (err) {
    console.error('[DesignResume] Failed to resume:', err);
    onError?.(errorMessage(err, 'Failed to continue. Please try again.'));
  } finally {
    window.dispatchEvent(new globalThis.CustomEvent('design_resume_resolved', {
      detail: { ok, entryId: detail?.entryId ?? null },
    }));
  }
};

/** Headless listener for resume_design_continue events from every Continue
 *  button in the app. The backend still validates project state, while the
 *  optional detail preserves the review gate the user is advancing from. */
const useDesignResumeListener = (
  projectId: string | null,
  activeSessionId: string | null,
  enabled: boolean,
) => {
  const { error: toastError } = useToast();

  useEffect(() => {
    if (!projectId) return;
    const handler = (event: Event) => {
      const detail = (event as globalThis.CustomEvent<DesignResumeDetail>).detail;

      // Read-only sessions can't resume, but Continue dispatchers are still
      // clickable — resolve with ok:false so the button un-spins instead of
      // showing "Continuing..." forever (mirrors resolveBuildAsReadOnly).
      if (!enabled) {
        window.dispatchEvent(new globalThis.CustomEvent('design_resume_resolved', {
          detail: { ok: false, entryId: detail?.entryId ?? null },
        }));

        return;
      }

      handleDesignResume(projectId, activeSessionId, detail, toastError);
    };

    window.addEventListener('resume_design_continue', handler);

    return () => window.removeEventListener('resume_design_continue', handler);
  }, [activeSessionId, enabled, projectId, toastError]);
};

function useActiveChatSession(projectId: string | null, routeSessionId?: string | null): {
  sessionId: string | null;
  isDeveloperMode: boolean;
  isLocked: boolean;
  isLockedByCurrentUser: boolean;
  canSendMessage: boolean;
  lockedByName: string | null;
  lockedByImageUrl: string | null;
  readOnlyReason: string | null;
} {
  const [selection, setSelection] = useState<{
    projectId: string | null;
    sessionId: string | null;
    isDeveloperMode: boolean;
    isLocked: boolean;
    isLockedByCurrentUser: boolean;
    canSendMessage: boolean;
    lockedByName: string | null;
    lockedByImageUrl: string | null;
    readOnlyReason: string | null;
  }>({
    projectId,
    sessionId: routeSessionId ?? null,
    isDeveloperMode: false,
    isLocked: false,
    isLockedByCurrentUser: false,
    canSendMessage: true,
    lockedByName: null,
    lockedByImageUrl: null,
    readOnlyReason: null,
  });

  useEffect(() => {
    if (!projectId) {
      setSelection({
        projectId: null,
        sessionId: null,
        isDeveloperMode: false,
        isLocked: false,
        isLockedByCurrentUser: false,
        canSendMessage: true,
        lockedByName: null,
        lockedByImageUrl: null,
        readOnlyReason: null,
      });

      return;
    }

    setSelection({
      projectId,
      sessionId: routeSessionId ?? null,
      isDeveloperMode: false,
      isLocked: false,
      isLockedByCurrentUser: false,
      canSendMessage: true,
      lockedByName: null,
      lockedByImageUrl: null,
      readOnlyReason: null,
    });
  }, [projectId, routeSessionId]);

  useEffect(() => {
    const onSelected = (event: Event) => {
      const detail = (event as globalThis.CustomEvent).detail as {
        projectId?: string | null;
        sessionId?: string | null;
        isDeveloperMode?: boolean;
        isLocked?: boolean;
        isLockedByCurrentUser?: boolean;
        canSendMessage?: boolean;
        lockedByName?: string | null;
        lockedByImageUrl?: string | null;
        readOnlyReason?: string | null;
      } | undefined;

      if (detail?.projectId && detail.projectId !== projectId) return;
      setSelection({
        projectId: detail?.projectId ?? projectId,
        sessionId: detail?.sessionId ?? null,
        isDeveloperMode: Boolean(detail?.isDeveloperMode),
        isLocked: Boolean(detail?.isLocked),
        isLockedByCurrentUser: Boolean(detail?.isLockedByCurrentUser),
        canSendMessage: detail?.canSendMessage !== false,
        lockedByName: detail?.lockedByName ?? null,
        lockedByImageUrl: detail?.lockedByImageUrl ?? null,
        readOnlyReason: detail?.readOnlyReason ?? null,
      });
    };

    window.addEventListener('chat_session_selected', onSelected);

    return () => window.removeEventListener('chat_session_selected', onSelected);
  }, [projectId]);

  const selectedSessionId = routeSessionId !== undefined
    ? routeSessionId
    : selection.projectId === projectId ? selection.sessionId : null;
  const hasSelectedMetadata = selection.projectId === projectId
    && selection.sessionId === selectedSessionId;

  return {
    sessionId: selectedSessionId,
    isDeveloperMode: hasSelectedMetadata ? selection.isDeveloperMode : false,
    isLocked: hasSelectedMetadata ? selection.isLocked : false,
    isLockedByCurrentUser: hasSelectedMetadata ? selection.isLockedByCurrentUser : false,
    canSendMessage: hasSelectedMetadata ? selection.canSendMessage : true,
    lockedByName: hasSelectedMetadata ? selection.lockedByName : null,
    lockedByImageUrl: hasSelectedMetadata ? selection.lockedByImageUrl : null,
    readOnlyReason: hasSelectedMetadata ? selection.readOnlyReason : null,
  };
}

/** Listener for 'submit_user_message' events — lets components outside the
 *  chat input (e.g. the ItemApprovalBar's "Specify Edits" modal) send a user
 *  message programmatically through the same streaming pipeline. */
const useProgrammaticSendListener = (onSend: (val: string, mediaUrls?: string[]) => void) => {
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as globalThis.CustomEvent).detail as { content?: string; mediaUrls?: string[] } | undefined;
      const content = detail?.content?.trim();

      if (!content) return;
      onSend(content, detail?.mediaUrls?.length ? detail.mediaUrls : undefined);
    };

    window.addEventListener('submit_user_message', handler);

    return () => window.removeEventListener('submit_user_message', handler);
  }, [onSend]);
};

/** Listener for 'prefill_chat_input' — seeds the chat input with a text
 *  stub (e.g. "Please make the following changes to {label}:") and focuses
 *  the textarea at the end, without sending. Fired by components like
 *  ItemApprovalBar's Specify Edits after its fly-to-input animation. */
const usePrefillListener = (
  onInputChange: (val: string) => void,
  inputRef: React.RefObject<HTMLTextAreaElement | null>,
) => {
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as globalThis.CustomEvent).detail as { content?: string } | undefined;
      const content = detail?.content ?? '';

      onInputChange(content);
      window.requestAnimationFrame(() => {
        const el = inputRef.current;

        if (!el) return;
        el.focus();
        const pos = content.length;

        el.selectionStart = pos;
        el.selectionEnd = pos;
      });
    };

    window.addEventListener('prefill_chat_input', handler);

    return () => window.removeEventListener('prefill_chat_input', handler);
  }, [onInputChange, inputRef]);
};

const useDeveloperModeProcessing = (
  projectId: string | null,
  sessionId: string | null,
): boolean => {
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    setProcessing(false);
  }, [projectId, sessionId]);

  useEffect(() => {
    if (!projectId || !sessionId) return undefined;

    const unsubscribe = eventService.subscribe('developer_mode_status', (data: unknown) => {
      const payload = data as {
        project_id?: string | null;
        session_id?: string | null;
        status?: string | null;
      };

      if (payload.project_id !== projectId || payload.session_id !== sessionId) return;
      const status = (payload.status || '').toLowerCase();

      if (status === 'running' || status === 'syncing' || status === 'indexing') {
        setProcessing(true);
      } else if (
        status === 'complete'
        || status === 'completed'
        || status === 'failed'
        || status === 'idle'
        || status === 'cancelled'
        || status === 'needs_plan_feedback'
      ) {
        setProcessing(false);
      }
    });

    return unsubscribe;
  }, [projectId, sessionId]);

  return processing;
};

const useDeveloperModeAiQuota = (
  projectId: string | null,
  isDeveloperMode: boolean,
): AiCreditQuotaStatus | null => {
  const [status, setStatus] = useState<AiCreditQuotaStatus | null>(null);

  useEffect(() => {
    setStatus(null);
    if (!projectId || !isDeveloperMode) return undefined;
    let cancelled = false;

    teamService.getMyAiCreditQuota(projectId)
      .then((quotaStatus) => {
        if (!cancelled) setStatus(quotaStatus);
      })
      .catch(() => {
        if (!cancelled) setStatus(null);
      });

    return () => { cancelled = true; };
  }, [isDeveloperMode, projectId]);

  useEffect(() => {
    if (!projectId || !isDeveloperMode) return undefined;

    return eventService.subscribe('credits_update', (data: unknown) => {
      const payload = data as {
        project_id?: string | null;
        reason?: string | null;
        ai_credit_quota?: number | null;
        ai_credits_used?: number | null;
        ai_credits_remaining?: number | null;
        ai_credit_quota_exhausted?: boolean | null;
        quota_required?: boolean | null;
      };

      if (payload.project_id !== projectId || payload.reason !== 'developer_mode_token_usage') return;
      setStatus((current) => ({
        project_id: projectId,
        user_id: current?.user_id ?? '',
        manager_user_id: current?.manager_user_id,
        manager_name: current?.manager_name,
        ai_credit_quota: Object.prototype.hasOwnProperty.call(payload, 'ai_credit_quota')
          ? payload.ai_credit_quota ?? null
          : current?.ai_credit_quota ?? null,
        ai_credits_used: payload.ai_credits_used ?? current?.ai_credits_used ?? 0,
        ai_credits_remaining: Object.prototype.hasOwnProperty.call(payload, 'ai_credits_remaining')
          ? payload.ai_credits_remaining ?? null
          : current?.ai_credits_remaining ?? null,
        ai_credit_quota_allocated: current?.ai_credit_quota_allocated ?? 0,
        ai_credit_quota_exhausted: Boolean(payload.ai_credit_quota_exhausted),
        quota_required: Boolean(payload.quota_required),
        can_manage_ai_credit_quota: current?.can_manage_ai_credit_quota,
      }));
    });
  }, [isDeveloperMode, projectId]);

  return status;
};

const QueuedDeveloperMessages: React.FC<{
  messages: QueuedDeveloperMessage[];
  onSteer: (messageId: string) => void;
  onEdit: (messageId: string, content: string) => void;
  onDelete: (messageId: string) => void;
  onReorder: (orderedIds: string[]) => void;
}> = ({ messages, onSteer, onEdit, onDelete, onReorder }) => {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [draggingId, setDraggingId] = useState<string | null>(null);

  if (!messages.length) return null;

  const beginEdit = (message: QueuedDeveloperMessage) => {
    setEditingId(message.id);
    setEditingValue(message.content);
    setOpenMenuId(null);
  };

  const saveEdit = () => {
    if (!editingId) return;
    const nextContent = editingValue.trim();
    if (nextContent) onEdit(editingId, nextContent);
    setEditingId(null);
    setEditingValue('');
  };

  const dropOn = (targetId: string) => {
    if (!draggingId || draggingId === targetId) return;
    const next = [...messages];
    const from = next.findIndex((message) => message.id === draggingId);
    const to = next.findIndex((message) => message.id === targetId);
    if (from < 0 || to < 0) return;
    const [dragged] = next.splice(from, 1);
    next.splice(to, 0, dragged);
    onReorder(next.map((message) => message.id));
    setDraggingId(null);
  };

  return (
    <div className="developer-queued-messages" aria-label="Queued developer-mode messages">
      {messages.map((message) => (
        <div
          key={message.id}
          className={`developer-queued-message${draggingId === message.id ? ' developer-queued-message--dragging' : ''}`}
          draggable
          onDragStart={() => setDraggingId(message.id)}
          onDragEnd={() => setDraggingId(null)}
          onDragOver={(event) => event.preventDefault()}
          onDrop={() => dropOn(message.id)}
        >
          <span className="developer-queued-message__handle" aria-hidden="true">↕</span>
          {editingId === message.id ? (
            <div className="developer-queued-message__edit">
              <textarea
                value={editingValue}
                onChange={(event) => setEditingValue(event.target.value)}
                rows={2}
                autoFocus
              />
              <div className="developer-queued-message__edit-actions">
                <button type="button" onClick={saveEdit}>Save</button>
                <button type="button" onClick={() => setEditingId(null)}>Cancel</button>
              </div>
            </div>
          ) : (
            <span className="developer-queued-message__text">
              {message.content}
            </span>
          )}
          <button
            type="button"
            className="developer-queued-message__steer"
            onClick={() => onSteer(message.id)}
            disabled={message.state === 'steer'}
            title={message.state === 'steer' ? 'This message will steer next' : 'Steer before next tool call'}
          >
            ↪ {message.state === 'steer' ? 'Steering' : 'Steer'}
          </button>
          <div className="developer-queued-message__menu">
            <button
              type="button"
              className="developer-queued-message__menu-button"
              onClick={() => setOpenMenuId(openMenuId === message.id ? null : message.id)}
              aria-label="Queued message actions"
            >
              ⋯
            </button>
            {openMenuId === message.id && (
              <div className="developer-queued-message__menu-popover">
                <button type="button" onClick={() => beginEdit(message)}>Edit</button>
                <button type="button" onClick={() => onDelete(message.id)}>Delete</button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export function ChatBoxInputArea({
  isLoading, onInputChange, onSend, onAgentLogsClick,
  inputRef, projectId, sessionId, isAnyAgentRunning, isPlanningRunning,
  hasTodoTasks, userMessageCount,
  isReadOnly = false,
}: ChatBoxInputAreaProps): React.ReactElement {
  const { user } = useAuth();
  const inputValue = useChatInputValue();
  const { hasByokKeys, refreshKeys } = useBYOKKeys(user?.hasByokKeys ?? false);
  const [showSettings, setShowSettings] = useState(false);
  const activeSession = useActiveChatSession(projectId, sessionId);
  const activeSessionId = activeSession.sessionId;
  const sessionLockedForCurrentUser = Boolean(
    (activeSession.isLocked && !activeSession.isLockedByCurrentUser)
    || activeSession.canSendMessage === false,
  );
  const developerModeProcessing = useDeveloperModeProcessing(
    projectId,
    activeSession.isDeveloperMode ? activeSessionId : null,
  );
  const developerModeAiQuota = useDeveloperModeAiQuota(projectId, activeSession.isDeveloperMode);
  const [developerModeStopping, setDeveloperModeStopping] = useState(false);
  const [planningCancelling, setPlanningCancelling] = useState(false);
  const [queuedDeveloperMessages, setQueuedDeveloperMessages] = useState<QueuedDeveloperMessage[]>([]);
  const subscriptionTier = (user?.subscription_tier || 'free').toLowerCase();
  const isByokPlan = subscriptionTier === 'byok';
  const [selectedModel, setSelectedModel] = useState<ModelTier>(() => getDefaultModel(subscriptionTier, hasByokKeys));
  const writableProjectId = isReadOnly ? null : projectId;

  useEffect(() => {
    setSelectedModel((currentModel) => {
      if (isByokPlan) {
        return isByokModel(currentModel) ? currentModel : getDefaultModel(subscriptionTier, hasByokKeys);
      }

      if (!hasByokKeys && isByokModel(currentModel)) {
        return getDefaultModel(subscriptionTier, hasByokKeys);
      }

      return currentModel;
    });
  }, [subscriptionTier, hasByokKeys, isByokPlan]);

  const { showUpgradeModal, setShowUpgradeModal } = useHeadlessBuildListener(
    writableProjectId, selectedModel, hasByokKeys, () => setShowSettings(true), !isReadOnly,
  );

  useEffect(() => {
    setQueuedDeveloperMessages((prev) => prev.filter((message) => message.session_id === activeSessionId));
  }, [activeSessionId]);

  useEffect(() => {
    if (!projectId || !activeSessionId || !activeSession.isDeveloperMode) {
      setQueuedDeveloperMessages([]);
      return;
    }

    let cancelled = false;
    projectService.listQueuedDeveloperModeMessages(projectId, activeSessionId)
      .then((messages) => {
        if (!cancelled) {
          setQueuedDeveloperMessages(messages.map((message) => ({
            id: message.id,
            session_id: message.session_id,
            content: message.content,
            state: message.state,
          })));
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [activeSession.isDeveloperMode, activeSessionId, projectId]);

  useEffect(() => {
    if (!developerModeProcessing) {
      setDeveloperModeStopping(false);
    }
  }, [developerModeProcessing]);

  useEffect(() => {
    if (!isPlanningRunning) {
      setPlanningCancelling(false);
    }
  }, [isPlanningRunning]);

  useEffect(() => {
    if (!projectId || !activeSessionId) return undefined;

    const removeOrUpdate = (data: unknown) => {
      const payload = data as {
        id?: string;
        project_id?: string | null;
        session_id?: string | null;
        content?: string | null;
        state?: string | null;
        ordered_ids?: string[] | null;
      };

      if (payload.project_id !== projectId || payload.session_id !== activeSessionId) return;
      if (payload.ordered_ids?.length) {
        setQueuedDeveloperMessages((prev) => {
          const order = new Map(payload.ordered_ids?.map((id, index) => [id, index]));
          return [...prev].sort((a, b) => (order.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (order.get(b.id) ?? Number.MAX_SAFE_INTEGER));
        });
        return;
      }
      if (!payload.id) return;
      const queuedId = payload.id;
      const queuedSessionId = activeSessionId;

      setQueuedDeveloperMessages((prev) => {
        if (payload.state === 'consumed' || payload.state === 'cancelled') {
          return prev.filter((message) => message.id !== queuedId);
        }

        if (prev.some((message) => message.id === queuedId)) {
          return prev.map((message) => (
            message.id === queuedId
              ? {
                ...message,
                content: payload.content || message.content,
                state: payload.state || message.state,
              }
              : message
          ));
        }

        return [
          ...prev,
          {
            id: queuedId,
            session_id: queuedSessionId,
            content: payload.content || 'Queued message',
            state: payload.state || 'queued',
          },
        ];
      });
    };

    const unsubQueued = eventService.subscribe('developer_mode_queued_message', removeOrUpdate);
    const unsubUpdated = eventService.subscribe('developer_mode_queued_message_update', removeOrUpdate);

    return () => {
      unsubQueued();
      unsubUpdated();
    };
  }, [activeSessionId, projectId]);

  const handleSend = useCallback(async (value: string, mediaUrls?: string[]) => {
    if (isReadOnly) return;
    if (sessionLockedForCurrentUser) return;
    if (
      activeSession.isDeveloperMode
      && (developerModeAiQuota?.ai_credit_quota_exhausted || developerModeAiQuota?.quota_required)
    ) {
      return;
    }
    const content = value.trim();

    if (
      !activeSession.isDeveloperMode
      && projectId
      && content
      && await invokeExactPlanningAction({
        text: content,
        projectId,
        sessionId: activeSessionId,
        mediaUrls,
      })
    ) {
      onInputChange('');
      return;
    }

    if (
      activeSession.isDeveloperMode
      && developerModeProcessing
      && projectId
      && activeSessionId
      && content
    ) {
      const queued = await projectService.queueDeveloperModeMessage(projectId, {
        session_id: activeSessionId,
        content,
        media_urls: mediaUrls ?? [],
      });

      setQueuedDeveloperMessages((prev) => [
        ...prev,
        {
          id: queued.id,
          session_id: queued.session_id,
          content: queued.content,
          state: queued.state,
        },
      ]);

      onInputChange('');
      window.dispatchEvent(new Event('chat_sessions_changed'));
      return;
    }

    onSend(value, mediaUrls);
  }, [
    activeSession.isDeveloperMode,
    activeSessionId,
    developerModeAiQuota?.ai_credit_quota_exhausted,
    developerModeAiQuota?.quota_required,
    developerModeProcessing,
    isReadOnly,
    onInputChange,
    onSend,
    projectId,
    sessionLockedForCurrentUser,
  ]);

  const handleSteer = useCallback((messageId: string) => {
    if (!projectId || !activeSessionId) return;
    setQueuedDeveloperMessages((prev) => prev.map((message) => (
      message.id === messageId ? { ...message, state: 'steer' } : message
    )));
    projectService.steerDeveloperModeMessage(projectId, activeSessionId, messageId).catch(() => {
      setQueuedDeveloperMessages((prev) => prev.map((message) => (
        message.id === messageId ? { ...message, state: 'queued' } : message
      )));
    });
  }, [activeSessionId, projectId]);

  const handleEditQueuedMessage = useCallback((messageId: string, content: string) => {
    if (!projectId || !activeSessionId) return;
    const previous = queuedDeveloperMessages;
    setQueuedDeveloperMessages((prev) => prev.map((message) => (
      message.id === messageId ? { ...message, content } : message
    )));
    projectService.updateQueuedDeveloperModeMessage(projectId, activeSessionId, messageId, content).catch(() => {
      setQueuedDeveloperMessages(previous);
    });
  }, [activeSessionId, projectId, queuedDeveloperMessages]);

  const handleDeleteQueuedMessage = useCallback((messageId: string) => {
    if (!projectId || !activeSessionId) return;
    const previous = queuedDeveloperMessages;
    setQueuedDeveloperMessages((prev) => prev.filter((message) => message.id !== messageId));
    projectService.deleteQueuedDeveloperModeMessage(projectId, activeSessionId, messageId).catch(() => {
      setQueuedDeveloperMessages(previous);
    });
  }, [activeSessionId, projectId, queuedDeveloperMessages]);

  const handleReorderQueuedMessages = useCallback((orderedIds: string[]) => {
    if (!projectId || !activeSessionId) return;
    const previous = queuedDeveloperMessages;
    const order = new Map(orderedIds.map((id, index) => [id, index]));
    setQueuedDeveloperMessages((prev) => [...prev].sort((a, b) => (
      (order.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (order.get(b.id) ?? Number.MAX_SAFE_INTEGER)
    )));
    projectService.reorderQueuedDeveloperModeMessages(projectId, activeSessionId, orderedIds).catch(() => {
      setQueuedDeveloperMessages(previous);
    });
  }, [activeSessionId, projectId, queuedDeveloperMessages]);

  const handleStopDeveloperMode = useCallback(async () => {
    if (!projectId || !activeSessionId || !activeSession.isDeveloperMode) return;
    setDeveloperModeStopping(true);
    try {
      await projectService.cancelDeveloperModeProcessing(projectId, { session_id: activeSessionId });
      window.dispatchEvent(new Event('chat_sessions_changed'));
    } catch (error) {
      console.error('Failed to stop developer mode:', error);
      setDeveloperModeStopping(false);
    }
  }, [activeSession.isDeveloperMode, activeSessionId, projectId]);

  const handleStopPlanning = useCallback(async () => {
    if (!projectId || planningCancelling) return;
    setPlanningCancelling(true);
    try {
      const result = await projectService.cancelAgents(projectId, activeSessionId);
      emitAgentCancelEvents(projectId, activeSessionId, result.cancelled_phase);
    } catch (error) {
      console.error('Failed to stop planning agents:', error);
    } finally {
      setPlanningCancelling(false);
    }
  }, [activeSessionId, planningCancelling, projectId]);

  useDesignResumeListener(projectId, activeSessionId, !isReadOnly);
  useProgrammaticSendListener(isReadOnly ? () => {} : handleSend);
  usePrefillListener(isReadOnly ? () => {} : onInputChange, inputRef);


  return (
    <>
      <ChatBoxInput
        ref={inputRef}
        value={inputValue} onChange={onInputChange} onSend={handleSend}
        isLoading={isLoading} onAgentLogsClick={onAgentLogsClick}
        projectId={projectId}
        sessionId={activeSessionId}
        isAnyAgentRunning={isAnyAgentRunning}
        hasTodoTasks={hasTodoTasks}
        isByokPlan={isByokPlan}
        isDeveloperMode={activeSession.isDeveloperMode}
        isDeveloperModeProcessing={developerModeProcessing && !sessionLockedForCurrentUser}
        isDeveloperModeStopping={developerModeStopping}
        isPlanningRunning={isPlanningRunning}
        isPlanningCancelling={planningCancelling}
        lockedByName={activeSession.lockedByName}
        lockedByImageUrl={activeSession.lockedByImageUrl}
        sessionLockReason={activeSession.readOnlyReason}
        sessionLocked={sessionLockedForCurrentUser}
        developerModeQuotaExhausted={Boolean(
          developerModeAiQuota?.ai_credit_quota_exhausted || developerModeAiQuota?.quota_required
        )}
        developerModeQuotaManagerName={developerModeAiQuota?.manager_name ?? null}
        onContactManager={() => {
          if (projectId) {
            window.location.assign(`/projects/${encodeURIComponent(projectId)}/team`);
          }
        }}
        onStopDeveloperMode={handleStopDeveloperMode}
        onStopPlanning={handleStopPlanning}
        userMessageCount={userMessageCount}
        onSettingsClick={() => setShowSettings(true)}
        queuedDeveloperMessagesSlot={!isReadOnly ? (
          <QueuedDeveloperMessages
            messages={queuedDeveloperMessages}
            onSteer={handleSteer}
            onEdit={handleEditQueuedMessage}
            onDelete={handleDeleteQueuedMessage}
            onReorder={handleReorderQueuedMessages}
          />
        ) : undefined}
        isReadOnly={isReadOnly}
      />
      <BYOKSettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        onKeysChange={refreshKeys}
      />
      {showUpgradeModal && (
        <UpgradeModal onClose={() => setShowUpgradeModal(false)} />
      )}
    </>
  );
}
